/**
 * Asset Behavior Report Page
 * Displays asset performance and customer interaction analytics
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  Assignment as AssignmentIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner } from '../../components/common';
import { AssetBehaviorService } from '../../services/reports/AssetBehaviorService';
import USMap from '../../components/reports/USMap';
import StateHeatmap from '../../components/reports/StateHeatmap';
import { formatDate } from '../../utils/dateUtils';
import type { AssetBehaviorData, KPICard, ReportFilters } from '../../types/reports';
import {
  Card,
  CardContent,
  TableCell,
  TableHeaderCell,
  TableRow,
  Select,
  PrimaryButton,
  SecondaryButton,
} from '../../components/ui';

const AssetBehaviorPage: React.FC = () => {
  const theme = useTheme();
  const [data, setData] = useState<AssetBehaviorData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [selectedStateName, setSelectedStateName] = useState<string | null>(null);
  const [stateHeatmapData, setStateHeatmapData] = useState<AssetBehaviorData['stateHeatmapData'] | null>(null);
  const [isLoadingHeatmap, setIsLoadingHeatmap] = useState(false);
  const [filters, setFilters] = useState<ReportFilters>({
    dateRange: 'Last 30 Days',
    region: 'Global',
  });

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await AssetBehaviorService.getAssetBehaviorData(filters);
      setData(result);
    } catch (error) {
      console.error('Failed to fetch asset behavior data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStateSelect = useCallback(async (stateCode: string, stateName: string) => {
    console.log('State selected:', stateCode, stateName); // Debug log
    setSelectedState(stateCode);
    setSelectedStateName(stateName);
    setIsLoadingHeatmap(true);

    try {
      const heatmapData = await AssetBehaviorService.getStateHeatmapData(stateCode, stateName);
      console.log('Heatmap data received:', heatmapData); // Debug log
      setStateHeatmapData(heatmapData);
    } catch (error) {
      console.error('Failed to fetch state heatmap data:', error);
      setStateHeatmapData(null);
    } finally {
      setIsLoadingHeatmap(false);
    }
  }, []);

  const handleCloseHeatmap = useCallback(() => {
    setSelectedState(null);
    setSelectedStateName(null);
    setStateHeatmapData(null);
  }, []);

  const getTrendIcon = (trend?: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon sx={{ fontSize: 14 }} />;
      case 'down':
        return <TrendingDownIcon sx={{ fontSize: 14 }} />;
      default:
        return <RemoveIcon sx={{ fontSize: 14 }} />;
    }
  };

  const getTrendColor = (trend?: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return theme.palette.success.main;
      case 'down':
        return theme.palette.error.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getFunnelIcon = (icon?: string) => {
    switch (icon) {
      case 'assignment':
        return <AssignmentIcon sx={{ fontSize: 20 }} />;
      case 'play':
        return <PlayArrowIcon sx={{ fontSize: 20 }} />;
      case 'process':
        return <RefreshIcon sx={{ fontSize: 20 }} />;
      case 'report':
        return <DescriptionIcon sx={{ fontSize: 20 }} />;
      default:
        return <CheckCircleIcon sx={{ fontSize: 20 }} />;
    }
  };

  const renderKPICard = (kpi: KPICard) => (
    <Card
      sx={{
        border: `1px solid ${theme.customColors.tableBorder}`,
        height: '100%',
      }}
    >
      <CardContent>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            marginBottom: theme.spacing(1),
            fontWeight: 500,
          }}
        >
          {kpi.title}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(1),
          }}
        >
          {kpi.value}
        </Typography>
        {kpi.change && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
            <Box sx={{ color: getTrendColor(kpi.trend) }}>
              {getTrendIcon(kpi.trend)}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: getTrendColor(kpi.trend),
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            >
              {kpi.change}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderFunnelStage = (stage: any, index: number, maxCount: number) => {
    const widthPercent = (stage.count / maxCount) * 100;
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.info.main,
    ];

    return (
      <Box
        key={stage.stage}
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: theme.spacing(2),
        }}
      >
        <Box
          sx={{
            width: `${widthPercent}%`,
            backgroundColor: colors[index % colors.length],
            padding: theme.spacing(2),
            borderRadius: theme.shape.borderRadius,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 60,
            transition: 'all 0.3s ease',
            '&:hover': {
              opacity: 0.9,
              transform: 'translateX(4px)',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1.5) }}>
            <Box sx={{ color: theme.palette.common.white }}>
              {getFunnelIcon(stage.icon)}
            </Box>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.common.white,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              >
                {stage.stage}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.common.white,
                  opacity: 0.9,
                  fontSize: '0.75rem',
                }}
              >
                {stage.count.toLocaleString()} ({stage.percentage}%)
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  if (isLoading && !data) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
          <LoadingSpinner />
        </Box>
      </MainLayout>
    );
  }

  if (!data) {
    return (
      <MainLayout>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No data available.
        </Typography>
      </MainLayout>
    );
  }

  const maxFunnelCount = Math.max(...data.conversionFunnel.map(s => s.count));

  return (
    <MainLayout>
      {/* Page Header */}
      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(0.5),
          }}
        >
          Asset Behavior Report
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(3),
          }}
        >
          A comprehensive overview of asset performance and customer interaction.
        </Typography>

        {/* Filters */}
        <Box
          sx={{
            display: 'flex',
            gap: theme.spacing(2),
            marginBottom: theme.spacing(3),
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Select
            size="small"
            label="Date"
            value={filters.dateRange || 'Last 30 Days'}
            onChange={(e) => setFilters({ ...filters, dateRange: e.target.value as string })}
            formControlProps={{ sx: { minWidth: 150 } }}
          >
            <MenuItem value="Last 24 Hours">Last 24 Hours</MenuItem>
            <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
            <MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
            <MenuItem value="Last 90 Days">Last 90 Days</MenuItem>
            <MenuItem value="Custom">Custom</MenuItem>
          </Select>

          <Select
            size="small"
            label="Asset Type"
            value="All"
            formControlProps={{ sx: { minWidth: 150 } }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Corporate">Corporate</MenuItem>
            <MenuItem value="Individual">Individual</MenuItem>
          </Select>

          <Select
            size="small"
            label="Customer Segment"
            value="All"
            formControlProps={{ sx: { minWidth: 180 } }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="High-Value">High-Value</MenuItem>
            <MenuItem value="Regular">Regular</MenuItem>
            <MenuItem value="New">New</MenuItem>
          </Select>

          <Select
            size="small"
            label="Region"
            value={filters.region || 'Global'}
            onChange={(e) => setFilters({ ...filters, region: e.target.value as string })}
            formControlProps={{ sx: { minWidth: 120 } }}
          >
            <MenuItem value="Global">Global</MenuItem>
            <MenuItem value="Michigan">Michigan</MenuItem>
            <MenuItem value="National">National</MenuItem>
          </Select>

          <SecondaryButton
            size="small"
            onClick={() => {
              setFilters({
                dateRange: 'Last 30 Days',
                region: 'Global',
              });
            }}
          >
            Reset
          </SecondaryButton>
          <PrimaryButton
            size="small"
            onClick={fetchData}
          >
            Apply Filters
          </PrimaryButton>
        </Box>
      </Box>

      {/* Usage Metrics KPI Cards */}
      <Grid container spacing={3} sx={{ marginBottom: theme.spacing(4) }}>
        <Grid item xs={12} sm={6} md={3}>
          {renderKPICard(data.usageMetrics.totalActiveAssets)}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKPICard(data.usageMetrics.avgTasksPerAsset)}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKPICard(data.usageMetrics.repeatUsageRate)}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKPICard(data.usageMetrics.highValueCustomers)}
        </Grid>
      </Grid>

      {/* Data Visualizations */}
      <Grid container spacing={3}>
        {/* Conversion Funnel */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 400,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(3),
                  fontSize: '1rem',
                }}
              >
                Conversion Funnel
              </Typography>
              <Box>
                {data.conversionFunnel.map((stage, index) =>
                  renderFunnelStage(stage, index, maxFunnelCount)
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Tasks per Asset */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 400,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(2),
                  fontSize: '1rem',
                }}
              >
                Tasks per Asset
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={data.tasksPerAsset.slice(0, 20)}
                  margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.customColors.tableBorder} />
                  <XAxis
                    dataKey="assetName"
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.7rem' }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.customColors.tableBorder}`,
                      borderRadius: theme.shape.borderRadius,
                    }}
                    labelStyle={{ color: theme.palette.text.primary }}
                  />
                  <Bar dataKey="tasks" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Repeat Usage Trend */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 400,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(2),
                  fontSize: '1rem',
                }}
              >
                Repeat Usage Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.repeatUsageTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.customColors.tableBorder} />
                  <XAxis
                    dataKey="date"
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                    tickFormatter={(value) => formatDate(value)}
                  />
                  <YAxis
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                    domain={[0, 100]}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.customColors.tableBorder}`,
                      borderRadius: theme.shape.borderRadius,
                    }}
                    labelStyle={{ color: theme.palette.text.primary }}
                    formatter={(value: number) => `${value.toFixed(1)}%`}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={theme.palette.primary.main}
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Analysis Section */}
      <Grid container spacing={3} sx={{ marginTop: theme.spacing(3) }}>
        {/* Customer Analysis */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 400,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(0.5),
                  fontSize: '1rem',
                }}
              >
                Customer Analysis
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  marginBottom: theme.spacing(2),
                  fontSize: '0.75rem',
                }}
              >
                High-Value Customers
              </Typography>
              {data.highValueCustomers && data.highValueCustomers.length > 0 ? (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>CUSTOMER</TableHeaderCell>
                      <TableHeaderCell>TASKS</TableHeaderCell>
                      <TableHeaderCell>VALUE</TableHeaderCell>
                      <TableHeaderCell>STATUS</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.highValueCustomers.slice(0, 5).map((customer) => (
                      <TableRow key={customer.assetId}>
                        <TableCell>
                          {customer.assetName}
                        </TableCell>
                        <TableCell>
                          {customer.totalTasks}
                        </TableCell>
                        <TableCell>
                          ${customer.totalSpending.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={customer.status}
                            size="small"
                            sx={{
                              backgroundColor:
                                customer.status === 'Active'
                                  ? theme.palette.success.main
                                  : customer.status === 'At Risk'
                                    ? theme.palette.warning.main
                                    : theme.palette.error.main,
                              color: theme.palette.common.white,
                              fontWeight: 500,
                              fontSize: '0.75rem',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  No data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Customer Segmentation */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 400,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(2),
                  fontSize: '1rem',
                }}
              >
                Customer Segmentation
              </Typography>
              {data.customerSegmentation ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data.customerSegmentation}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ segment, percent }: any) => `${segment}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      innerRadius={40}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {data.customerSegmentation.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.customColors.tableBorder}`,
                        borderRadius: theme.shape.borderRadius,
                      }}
                      labelStyle={{ color: theme.palette.text.primary }}
                      formatter={(value: number) => `${value}%`}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  No data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Geographic Trends */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 400,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(0.5),
                  fontSize: '1rem',
                }}
              >
                Geographic Trends
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  marginBottom: theme.spacing(2),
                  fontSize: '0.75rem',
                }}
              >
                {selectedState ? `${selectedStateName} - City Heatmap` : 'Task Density by State'}
              </Typography>
              {selectedState && stateHeatmapData ? (
                <Box>
                  {isLoadingHeatmap ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 400,
                      }}
                    >
                      <LoadingSpinner />
                    </Box>
                  ) : (
                    <StateHeatmap
                      state={stateHeatmapData.state}
                      stateCode={stateHeatmapData.stateCode}
                      cities={stateHeatmapData.cities}
                      onClose={handleCloseHeatmap}
                    />
                  )}
                </Box>
              ) : data.usStatesData && data.usStatesData.length > 0 ? (
                <Box
                  sx={{
                    height: 350,
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  <USMap
                    data={data.usStatesData}
                    onStateSelect={handleStateSelect}
                    selectedState={selectedState || undefined}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 300,
                    color: theme.palette.text.secondary,
                  }}
                >
                  <Typography variant="body2">No data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Daily and Hourly Demand */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            {/* Daily Demand */}
            <Grid item xs={12}>
              <Card
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.customColors.tableBorder}`,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      marginBottom: theme.spacing(2),
                      fontSize: '1rem',
                    }}
                  >
                    Daily Demand
                  </Typography>
                  {data.dailyDemand ? (
                    <ResponsiveContainer width="100%" height={150}>
                      <BarChart data={data.dailyDemand}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.customColors.tableBorder} />
                        <XAxis
                          dataKey="day"
                          stroke={theme.palette.text.secondary}
                          style={{ fontSize: '0.7rem' }}
                        />
                        <YAxis
                          stroke={theme.palette.text.secondary}
                          style={{ fontSize: '0.7rem' }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.customColors.tableBorder}`,
                            borderRadius: theme.shape.borderRadius,
                          }}
                          labelStyle={{ color: theme.palette.text.primary }}
                        />
                        <Bar dataKey="value" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      No data available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Hourly Demand */}
            <Grid item xs={12}>
              <Card
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.customColors.tableBorder}`,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      marginBottom: theme.spacing(2),
                      fontSize: '1rem',
                    }}
                  >
                    Hourly Demand
                  </Typography>
                  {data.hourlyDemand ? (
                    <ResponsiveContainer width="100%" height={150}>
                      <BarChart data={data.hourlyDemand}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.customColors.tableBorder} />
                        <XAxis
                          dataKey="hour"
                          stroke={theme.palette.text.secondary}
                          style={{ fontSize: '0.75rem' }}
                          tickFormatter={(value) => `${value}:00`}
                        />
                        <YAxis
                          stroke={theme.palette.text.secondary}
                          style={{ fontSize: '0.7rem' }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.customColors.tableBorder}`,
                            borderRadius: theme.shape.borderRadius,
                          }}
                          labelStyle={{ color: theme.palette.text.primary }}
                          formatter={(value: number) => `${value} tasks`}
                        />
                        <Bar dataKey="value" fill={theme.palette.secondary.main} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      No data available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </MainLayout>
  );
};

export default AssetBehaviorPage;

